import * as XLSX from "xlsx-js-style"; // ← install: npm install xlsx-js-style
import { saveAs } from "file-saver";
import { Button } from "reactstrap";
import DownloadFileIcon from "../svg/DownloadFile";
import { useState } from "react";

type apiData = {
  getData: () => Promise<any[]>;
  ExcelSheetName: string;
  InstrumentName: string;
};

const DownloadExcel = ({
  getData,
  ExcelSheetName,
  InstrumentName,
}: apiData) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const data = await getData();
      if (!data || data.length === 0) return;

      const headers = Object.keys(data[0] || {});
      const worksheet = XLSX.utils.json_to_sheet([]);

      // ── Title row ─────────────────────────────────────────
      XLSX.utils.sheet_add_aoa(worksheet, [[InstrumentName]], { origin: "A1" });
      worksheet["A1"].s = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" }, name: "Arial" },
        fill: { fgColor: { rgb: "1E563D" }, patternType: "solid" },
        alignment: { horizontal: "center", vertical: "center" },
      };
      // merge title across all columns
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      ];

      // ── Header row (row 2) ─────────────────────────────────
      XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });
      headers.forEach((_, c) => {
        const cell = XLSX.utils.encode_cell({ r: 1, c });
        if (!worksheet[cell]) return;
        worksheet[cell].s = {
          font: { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 11 },
          fill: { fgColor: { rgb: "2D7A57" }, patternType: "solid" },
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
          border: {
            top: { style: "thin", color: { rgb: "FFFFFF" } },
            bottom: { style: "thin", color: { rgb: "FFFFFF" } },
            left: { style: "thin", color: { rgb: "FFFFFF" } },
            right: { style: "thin", color: { rgb: "FFFFFF" } },
          },
        };
      });

      // ── Data rows (starting row 3) ─────────────────────────
      const rows = data.map((row) => headers.map((h) => row[h] ?? "-"));
      XLSX.utils.sheet_add_aoa(worksheet, rows, { origin: "A3" });

      rows.forEach((_, rowIdx) => {
        const isEven = rowIdx % 2 === 0;
        headers.forEach((_, c) => {
          const cell = XLSX.utils.encode_cell({ r: rowIdx + 2, c });
          if (!worksheet[cell]) worksheet[cell] = { v: "-", t: "s" };
          worksheet[cell].s = {
            font: { name: "Arial", sz: 10, color: { rgb: "1A1A1A" } },
            fill: {
              fgColor: { rgb: isEven ? "EAF4EE" : "FFFFFF" },
              patternType: "solid",
            },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "C8DDD1" } },
              bottom: { style: "thin", color: { rgb: "C8DDD1" } },
              left: { style: "thin", color: { rgb: "C8DDD1" } },
              right: { style: "thin", color: { rgb: "C8DDD1" } },
            },
          };
        });
      });

      // ── Column widths ──────────────────────────────────────
      worksheet["!cols"] = headers.map((key) => ({
        wch: Math.max(
          key.length + 4,
          ...data.map((row) => String(row[key] ?? "").length + 2),
          12,
        ),
      }));

      // ── Row heights ────────────────────────────────────────
      worksheet["!rows"] = [
        { hpt: 28 }, // title row
        { hpt: 22 }, // header row
        ...rows.map(() => ({ hpt: 18 })), // data rows
      ];

      // ── Freeze header ──────────────────────────────────────
      worksheet["!freeze"] = { xSplit: 0, ySplit: 2 }; // freeze title + header

      const workBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workBook,
        worksheet,
        ExcelSheetName.slice(0, 31),
      );

      const excelBuffer = XLSX.write(workBook, {
        type: "array",
        bookType: "xlsx",
        cellStyles: true,
      });

      saveAs(
        new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${ExcelSheetName}.xlsx`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={loading}
      className="download-button"
    >
      {loading ? (
        <span style={{ fontSize: "12px" }}>Loading...</span>
      ) : (
        <>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </>
      )}
    </Button>
  );
};

export default DownloadExcel;
